// src/Pages/CEOZPReport.jsx
import { useState, useEffect, useMemo } from "react";
import { Button, Table, Spin, Alert, message, Tag } from "antd";
import {
  FileExcelOutlined,
  ReloadOutlined,
  FilePdfOutlined,
} from "@ant-design/icons";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { getReportData } from "../api/User";

const CEOZPReport = ({ dateFilter }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Calculate totals for CURRENT PAGE
  const pageTotals = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        block_count: 0,
        panchayat_count: 0,
        total_gramsabha_conducted: 0,
        total_beneficiaries: 0,
        total_gs_yes: 0,
        total_gs_no: 0,
        total_gs_pending: 0,
      };
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentPageData = data.slice(startIndex, endIndex);

    if (currentPageData.length === 0) {
      return {
        block_count: 0,
        panchayat_count: 0,
        total_gramsabha_conducted: 0,
        total_beneficiaries: 0,
        total_gs_yes: 0,
        total_gs_no: 0,
        total_gs_pending: 0,
      };
    }

    return currentPageData.reduce(
      (acc, item) => {
        return {
          block_count: acc.block_count + (parseInt(item.block_count) || 0),
          panchayat_count:
            acc.panchayat_count + (parseInt(item.panchayat_count) || 0),
          total_gramsabha_conducted:
            acc.total_gramsabha_conducted +
            (parseInt(item.total_gramsabha_conducted) || 0),
          total_beneficiaries:
            acc.total_beneficiaries + (parseInt(item.total_beneficiaries) || 0),
          total_gs_yes: acc.total_gs_yes + (parseInt(item.total_gs_yes) || 0),
          total_gs_no: acc.total_gs_no + (parseInt(item.total_gs_no) || 0),
          total_gs_pending:
            acc.total_gs_pending + (parseInt(item.total_gs_pending) || 0),
        };
      },
      {
        block_count: 0,
        panchayat_count: 0,
        total_gramsabha_conducted: 0,
        total_beneficiaries: 0,
        total_gs_yes: 0,
        total_gs_no: 0,
        total_gs_pending: 0,
      }
    );
  }, [data, currentPage, pageSize]);

  const columns = [
    {
      title: "SR.NO.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: 45,
      fixed: "left",
      align: "center",
    },
    {
      title: "District Name",
      dataIndex: "district_name",
      key: "district_name",
      width: 110,
      align: "center",
      sorter: (a, b) => a.district_name?.localeCompare(b.district_name),
      ellipsis: true,
    },
    {
      title: "Block Count",
      dataIndex: "block_count",
      key: "block_count",
      width: 70,
      align: "center",
      sorter: (a, b) => a.block_count - b.block_count,
    },
    {
      title: "GP Count",
      dataIndex: "panchayat_count",
      key: "panchayat_count",
      width: 70,
      align: "center",
      sorter: (a, b) => a.panchayat_count - b.panchayat_count,
    },
    {
      title: "Total GS Conducted",
      dataIndex: "total_gramsabha_conducted",
      key: "total_gramsabha_conducted",
      width: 90,
      align: "center",
      sorter: (a, b) =>
        a.total_gramsabha_conducted - b.total_gramsabha_conducted,
    },
    {
      title: "Total Beneficiaries",
      dataIndex: "total_beneficiaries",
      key: "total_beneficiaries",
      width: 90,
      align: "center",
      sorter: (a, b) => a.total_beneficiaries - b.total_beneficiaries,
    },
    {
      title: "Income Verified (Yes)",
      dataIndex: "total_gs_yes",
      key: "total_gs_yes",
      width: 100,
      align: "center",
      sorter: (a, b) => a.total_gs_yes - b.total_gs_yes,
      render: (val) => (
        <Tag color="green" style={{ fontSize: "10px" }}>
          {val || 0}
        </Tag>
      ),
    },
    {
      title: "Not Verified (No)",
      dataIndex: "total_gs_no",
      key: "total_gs_no",
      width: 95,
      align: "center",
      sorter: (a, b) => a.total_gs_no - b.total_gs_no,
      render: (val) => (
        <Tag color="red" style={{ fontSize: "10px" }}>
          {val || 0}
        </Tag>
      ),
    },
    {
      title: "Pending",
      dataIndex: "total_gs_pending",
      key: "total_gs_pending",
      width: 70,
      align: "center",
      sorter: (a, b) => a.total_gs_pending - b.total_gs_pending,
      render: (val) => (
        <Tag color="orange" style={{ fontSize: "10px" }}>
          {val || 0}
        </Tag>
      ),
    },
  ];

  useEffect(() => {
    fetchReportData();
  }, [dateFilter]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await getReportData(token, dateFilter);

      if (response.status === "success") {
        const formattedData = Array.isArray(response.data)
          ? response.data.map((item, index) => ({
            ...item,
            key: item.district_code || `row-${index}`,
            sr_no: index + 1,
          }))
          : [];

        setData(formattedData);
        message.success(`Loaded ${formattedData.length} districts`);
      } else {
        setError(response.message || "Failed to fetch report data");
      }
    } catch (err) {
      console.error("Error fetching report data:", err);
      setError(err.message || "An error occurred while fetching data");
      message.error("Failed to load report data");
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };


  // Calculate totals for ALL DATA (for exports)
  const overallTotals = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        block_count: 0,
        panchayat_count: 0,
        total_gramsabha_conducted: 0,
        total_beneficiaries: 0,
        total_gs_yes: 0,
        total_gs_no: 0,
        total_gs_pending: 0,
      };
    }

    return data.reduce(
      (acc, item) => {
        return {
          block_count: acc.block_count + (parseInt(item.block_count) || 0),
          panchayat_count: acc.panchayat_count + (parseInt(item.panchayat_count) || 0),
          total_gramsabha_conducted:
            acc.total_gramsabha_conducted + (parseInt(item.total_gramsabha_conducted) || 0),
          total_beneficiaries:
            acc.total_beneficiaries + (parseInt(item.total_beneficiaries) || 0),
          total_gs_yes: acc.total_gs_yes + (parseInt(item.total_gs_yes) || 0),
          total_gs_no: acc.total_gs_no + (parseInt(item.total_gs_no) || 0),
          total_gs_pending: acc.total_gs_pending + (parseInt(item.total_gs_pending) || 0),
        };
      },
      {
        block_count: 0,
        panchayat_count: 0,
        total_gramsabha_conducted: 0,
        total_beneficiaries: 0,
        total_gs_yes: 0,
        total_gs_no: 0,
        total_gs_pending: 0,
      }
    );
  }, [data]);
  const handleExportPDF = async () => {
    try {
      setLoading(true);

      const token = sessionStorage.getItem("token");
      if (!token) {
        message.error("Please login again");
        return;
      }

      const response = await getReportData(token, dateFilter);
      const reportData = response?.data || [];

      if (!reportData.length) {
        message.warning("No data to export");
        return;
      }

      // ✅ Calculate overall totals
      const overallTotals = reportData.reduce(
        (acc, item) => ({
          block_count: acc.block_count + (parseInt(item.block_count) || 0),
          panchayat_count: acc.panchayat_count + (parseInt(item.panchayat_count) || 0),
          total_gramsabha_conducted:
            acc.total_gramsabha_conducted + (parseInt(item.total_gramsabha_conducted) || 0),
          total_beneficiaries:
            acc.total_beneficiaries + (parseInt(item.total_beneficiaries) || 0),
          total_gs_yes: acc.total_gs_yes + (parseInt(item.total_gs_yes) || 0),
          total_gs_no: acc.total_gs_no + (parseInt(item.total_gs_no) || 0),
          total_gs_pending: acc.total_gs_pending + (parseInt(item.total_gs_pending) || 0),
        }),
        {
          block_count: 0,
          panchayat_count: 0,
          total_gramsabha_conducted: 0,
          total_beneficiaries: 0,
          total_gs_yes: 0,
          total_gs_no: 0,
          total_gs_pending: 0,
        }
      );

      // ✅ Landscape A4
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(14);
      doc.text("District Wise Consolidated Report", pageWidth / 2, 12, {
        align: "center",
      });

      doc.setFontSize(9);
      doc.text(
        `Generated on: ${new Date().toLocaleString()}`,
        pageWidth / 2,
        18,
        { align: "center" }
      );

      // ✅ Table headers
      const headers = columns.map((col) => col.title);

      // ✅ Table body
      const body = reportData.map((row, index) =>
        columns.map((col) => {
          if (col.dataIndex === "sr_no") return index + 1;
          return row[col.dataIndex] ?? "";
        })
      );

      // ✅ Add total row to body
      body.push([
        "GRAND TOTAL",
        "",
        overallTotals.block_count.toLocaleString(),
        overallTotals.panchayat_count.toLocaleString(),
        overallTotals.total_gramsabha_conducted.toLocaleString(),
        overallTotals.total_beneficiaries.toLocaleString(),
        overallTotals.total_gs_yes.toLocaleString(),
        overallTotals.total_gs_no.toLocaleString(),
        overallTotals.total_gs_pending.toLocaleString(),
      ]);

      autoTable(doc, {
        startY: 24,
        head: [headers],
        body: body,
        theme: "grid",
        styles: {
          fontSize: 7,
          cellPadding: 2,
          overflow: "linebreak",
          halign: "center",
          valign: "middle",
        },
        headStyles: {
          fillColor: [22, 119, 255],
          textColor: 255,
          fontStyle: "bold",
        },
        // ✅ Style the total row differently
        bodyStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
        // Apply special styling to the last row (total row)
        didParseCell: function (data) {
          if (data.row.index === reportData.length) {
            data.cell.styles.fillColor = [240, 249, 255];
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.textColor = [0, 0, 0];
          }
        },
        tableWidth: "auto",
        margin: { left: 5, right: 5 },
      });

      doc.save(`District_Consolidated_Report_${Date.now()}.pdf`);
      message.success("PDF downloaded successfully");
    } catch (error) {
      console.error(error);
      message.error("PDF export failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      // ✅ Calculate overall totals
      const overallTotals = data.reduce(
        (acc, item) => ({
          block_count: acc.block_count + (parseInt(item.block_count) || 0),
          panchayat_count: acc.panchayat_count + (parseInt(item.panchayat_count) || 0),
          total_gramsabha_conducted:
            acc.total_gramsabha_conducted + (parseInt(item.total_gramsabha_conducted) || 0),
          total_beneficiaries:
            acc.total_beneficiaries + (parseInt(item.total_beneficiaries) || 0),
          total_gs_yes: acc.total_gs_yes + (parseInt(item.total_gs_yes) || 0),
          total_gs_no: acc.total_gs_no + (parseInt(item.total_gs_no) || 0),
          total_gs_pending: acc.total_gs_pending + (parseInt(item.total_gs_pending) || 0),
        }),
        {
          block_count: 0,
          panchayat_count: 0,
          total_gramsabha_conducted: 0,
          total_beneficiaries: 0,
          total_gs_yes: 0,
          total_gs_no: 0,
          total_gs_pending: 0,
        }
      );

      const csvContent = [
        // Headers
        columns.map((col) => `"${col.title}"`).join(","),
        // Data rows
        ...data.map((row) =>
          columns
            .map((col) => {
              const value = row[col.dataIndex];
              return `"${value !== null && value !== undefined
                  ? value.toString().replace(/"/g, '""')
                  : ""
                }"`;
            })
            .join(",")
        ),
        // ✅ Total row
        `"GRAND TOTAL","",` +
        `"${overallTotals.block_count}",` +
        `"${overallTotals.panchayat_count}",` +
        `"${overallTotals.total_gramsabha_conducted}",` +
        `"${overallTotals.total_beneficiaries}",` +
        `"${overallTotals.total_gs_yes}",` +
        `"${overallTotals.total_gs_no}",` +
        `"${overallTotals.total_gs_pending}"`,
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `District_Report_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };
  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">District Wise Consolidated Report</h2>
            <p className="report-subtitle">
              Total Districts: <strong>{data.length}</strong>
            </p>
          </div>
          <div className="report-actions">
            <Button
              onClick={handleExportPDF}
              disabled={loading || data.length === 0}
              icon={<FilePdfOutlined />}
            >
              Export PDF
            </Button>
            <Button
              type="primary"
              onClick={handleExportExcel}
              disabled={loading || data.length === 0}
              icon={<FileExcelOutlined />}
              
            >
              Export Excel
            </Button>
            <Button
              onClick={fetchReportData}
              disabled={loading}
              icon={<ReloadOutlined />}
            
            >
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            closable
            onClose={() => setError(null)}
          />
        )}

        {loading ? (
          <div className="loading-container">
            <Spin size="large" tip="Loading report data..." />
          </div>
        ) : data.length > 0 ? (
          <>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="key"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                pageSizeOptions: ["10", "20", "50", "100"], // ✅ options
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} districts`,
                total: data.length,
              }}
              onChange={handleTableChange}
              scroll={{ x: 850, y: 420 }}
              bordered
              size="small"
            />

            {/* Total Row */}
            {/* Overall Total Row */}
            <div
             style={{
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: "4px",
                padding: "8px 12px",
                marginTop: "8px",
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                fontSize: "11px",
              }}
            >
              <span>
                <strong>Totals:</strong>
              </span>
              <span>
                Blocks: <strong>{overallTotals.block_count.toLocaleString()}</strong>
              </span>
              <span>
                GPs: <strong>{overallTotals.panchayat_count.toLocaleString()}</strong>
              </span>
              <span>
                GS Conducted:{" "}
                <strong>{overallTotals.total_gramsabha_conducted.toLocaleString()}</strong>
              </span>
              <span>
                Beneficiaries:{" "}
                <strong>{overallTotals.total_beneficiaries.toLocaleString()}</strong>
              </span>
              <span>
                Verified: <strong>{overallTotals.total_gs_yes.toLocaleString()}</strong>
              </span>
              <span>
                Not Verified:{" "}
                <strong>{overallTotals.total_gs_no.toLocaleString()}</strong>
              </span>
              <span>
                Pending:{" "}
                <strong>{overallTotals.total_gs_pending.toLocaleString()}</strong>
              </span>
            </div>
          </>
        ) : (
          <Alert
            message="No Data Available"
            description="No report data found. Click the Refresh button to try again."
            type="info"
            showIcon
            action={
              <Button size="small" type="primary" onClick={fetchReportData}>
                Refresh Data
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
};

export default CEOZPReport;
